import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  CheckCircle2,
  CopyPlus,
  FileText,
  Lock,
  Loader2,
  Save,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/cn";
import { canonicalApi, type CanonicalDetail, type ValidateResponse } from "@/lib/api-canonical";

import { CodeEditor, DiffViewer } from "./CodeEditor";
import { TestPayloadPanel } from "./TestPayloadPanel";

export interface CanonicalDetailPanelProps {
  detail: CanonicalDetail;
  previousActive: CanonicalDetail | null;
  canEdit: boolean;
  /** Deleting a draft is a SEPARATE permission (canonical:delete), not
   *  implied by edit (canonical:write). Gated independently so the
   *  button matches what the backend will actually allow. */
  canDelete: boolean;
  onDeleted: () => void;
  onNewVersion: (baseYaml: string, nextVersion: number, canonical_type: string) => void;
  highestVersion: number;
}

export function CanonicalDetailPanel({
  detail,
  previousActive,
  canEdit,
  canDelete,
  onDeleted,
  onNewVersion,
  highestVersion,
}: CanonicalDetailPanelProps) {
  const qc = useQueryClient();
  const isDraft = detail.status === "draft";
  const isActive = detail.status === "active";
  const editable = canEdit && isDraft;

  const [draft, setDraft] = React.useState(detail.yaml_definition);
  const [validation, setValidation] = React.useState<ValidateResponse | null>(null);

  React.useEffect(() => {
    setDraft(detail.yaml_definition);
    setValidation(null);
  }, [detail.canonical_type, detail.version, detail.yaml_definition]);

  // Debounced live validation
  React.useEffect(() => {
    if (!editable) return;
    const handle = setTimeout(async () => {
      try {
        const r = await canonicalApi.validate(draft);
        setValidation(r);
      } catch {
        // network noise — ignore
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [draft, editable]);

  const saveMutation = useMutation({
    mutationFn: () => canonicalApi.updateDraft(detail.canonical_type, detail.version, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["canonical"] });
      qc.invalidateQueries({
        queryKey: ["canonical", detail.canonical_type, detail.version],
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => canonicalApi.activate(detail.canonical_type, detail.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["canonical"] }),
  });

  const deprecateMutation = useMutation({
    mutationFn: () => canonicalApi.deprecate(detail.canonical_type, detail.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["canonical"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => canonicalApi.remove(detail.canonical_type, detail.version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["canonical"] });
      onDeleted();
    },
  });

  // Tags: optimistic local copy + debounced PATCH. Allowed on any
  // status (tags are metadata, not part of the immutable schema).
  const [tagsDraft, setTagsDraft] = React.useState<string[]>(detail.tags ?? []);
  React.useEffect(() => {
    setTagsDraft(detail.tags ?? []);
  }, [detail.canonical_type, detail.version, detail.tags]);
  const updateTagsMutation = useMutation({
    mutationFn: (next: string[]) =>
      canonicalApi.updateTags(detail.canonical_type, detail.version, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["canonical"] }),
  });
  React.useEffect(() => {
    const same =
      tagsDraft.length === (detail.tags ?? []).length &&
      tagsDraft.every((t, i) => t === (detail.tags ?? [])[i]);
    if (same) return;
    const h = setTimeout(() => updateTagsMutation.mutate(tagsDraft), 500);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsDraft]);

  const isDirty = draft !== detail.yaml_definition;

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex min-h-[3.25rem] items-center justify-between gap-3 border-b border-border/50 bg-surface px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{detail.canonical_type}</span>
              <span className="font-mono text-xs text-muted-foreground">v{detail.version}</span>
              <Badge tone={detail.status as never}>{detail.status}</Badge>
            </div>
            {/* One compact, non-wrapping line — date only (no seconds), full
                detail on hover. Activation/deprecation supersede "created" as
                the meaningful timestamp, so we show whichever applies. */}
            <div
              className="truncate text-xs text-muted-foreground"
              title={[
                `Created by ${detail.created_by} on ${new Date(detail.created_at).toLocaleString()}`,
                detail.activated_at && `Activated ${new Date(detail.activated_at).toLocaleString()}`,
                detail.deprecated_at && `Deprecated ${new Date(detail.deprecated_at).toLocaleString()}`,
              ]
                .filter(Boolean)
                .join("\n")}
            >
              {detail.created_by}
              {detail.deprecated_at
                ? ` · deprecated ${new Date(detail.deprecated_at).toLocaleDateString()}`
                : detail.activated_at
                  ? ` · activated ${new Date(detail.activated_at).toLocaleDateString()}`
                  : ` · created ${new Date(detail.created_at).toLocaleDateString()}`}
            </div>
          </div>
        </div>

        {(canEdit || canDelete) && (
          <div className="flex shrink-0 items-center gap-2">
            {isDraft && canEdit && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => saveMutation.mutate()}
                  disabled={!isDirty || saveMutation.isPending || !(validation?.valid ?? true)}
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <Save className="h-3.5 w-3.5" strokeWidth={1.5} />
                  )}
                  Save
                </Button>
                <Button
                  size="sm"
                  onClick={() => activateMutation.mutate()}
                  disabled={isDirty || activateMutation.isPending}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Activate
                </Button>
              </>
            )}
            {isDraft && canDelete && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  if (confirm(`Delete draft ${detail.canonical_type} v${detail.version}?`)) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
            )}
            {canEdit && (isActive || detail.status === "deprecated") && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  onNewVersion(detail.yaml_definition, highestVersion + 1, detail.canonical_type)
                }
              >
                <CopyPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
                New version
              </Button>
            )}
            {canEdit && isActive && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (confirm(`Deprecate active canonical ${detail.canonical_type} v${detail.version}?`)) {
                    deprecateMutation.mutate();
                  }
                }}
                disabled={deprecateMutation.isPending}
              >
                <Archive className="h-3.5 w-3.5" strokeWidth={1.5} />
                Deprecate
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tags strip — always editable (tags are metadata, not schema). */}
      <div className="flex items-center gap-3 border-b border-border/50 bg-surface px-4 py-2 text-xs">
        <span className="text-muted-foreground">Tags</span>
        <div className="flex-1">
          <TagsField
            value={tagsDraft}
            onChange={setTagsDraft}
            scope="canonicals"
            disabled={!canEdit}
            placeholder="e.g. domain:warehouse, shared"
          />
        </div>
        {updateTagsMutation.isPending && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" strokeWidth={1.5} />
        )}
      </div>

      {/* Immutability banner for active / deprecated */}
      {!isDraft && (
        <div className="border-b border-border/50 bg-surface px-4 py-2">
          <Alert
            tone={detail.status === "active" ? "info" : "warning"}
            title={
              detail.status === "active"
                ? "Active version — immutable"
                : "Deprecated version — immutable"
            }
          >
            <div className="flex items-center gap-2">
              <Lock className="h-3 w-3 shrink-0" strokeWidth={1.5} />
              <span>
                Active and deprecated canonicals cannot be edited or deleted by design.
                {canEdit && (
                  <>
                    {" "}
                    To evolve this schema, click <span className="font-medium">New version</span>{" "}
                    above to create a draft pre-populated from this YAML.
                  </>
                )}
              </span>
            </div>
          </Alert>
        </div>
      )}

      {/* Validation banner */}
      {editable && validation && !validation.valid && (
        <div className="border-b border-border/50 bg-surface px-4 py-2">
          <Alert tone="danger" title="Invalid YAML">
            <ul className="list-disc pl-4">
              {validation.errors.map((e, i) => (
                <li key={i} className="font-mono text-xs">{e}</li>
              ))}
            </ul>
          </Alert>
        </div>
      )}
      {editable && validation && validation.valid && isDirty && (
        <div className="border-b border-border/50 bg-surface px-4 py-2">
          <Alert tone="info" title="Unsaved changes">
            Valid YAML — click Save to persist, then Activate to promote.
          </Alert>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="yaml" className="flex flex-1 flex-col overflow-hidden">
        <TabsList className="px-4 bg-surface">
          <TabsTrigger value="yaml">YAML</TabsTrigger>
          <TabsTrigger value="schema">JSON Schema</TabsTrigger>
          <TabsTrigger value="test">Test payload</TabsTrigger>
          <TabsTrigger value="diff" disabled={!previousActive}>
            Diff
          </TabsTrigger>
        </TabsList>

        <TabsContent value="yaml" className="flex-1 overflow-hidden">
          <CodeEditor
            value={editable ? draft : detail.yaml_definition}
            onChange={editable ? setDraft : undefined}
            readOnly={!editable}
            language="yaml"
            className={cn(!editable && "opacity-95")}
          />
        </TabsContent>

        <TabsContent value="schema" className="flex-1 overflow-hidden">
          <CodeEditor
            value={JSON.stringify(detail.compiled_schema, null, 2)}
            language="json"
            readOnly
          />
        </TabsContent>

        <TabsContent value="test" className="flex-1 overflow-hidden">
          <TestPayloadPanel
            canonical_type={detail.canonical_type}
            version={detail.version}
          />
        </TabsContent>

        <TabsContent value="diff" className="flex-1 overflow-hidden">
          {previousActive ? (
            <DiffViewer
              original={previousActive.yaml_definition}
              modified={editable ? draft : detail.yaml_definition}
              language="yaml"
            />
          ) : (
            <div className="p-6 text-sm text-muted-foreground">
              No previous active version to compare against.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
